import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import { BsFilterCircle, BsSearch } from 'react-icons/bs';
import styled, { keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import useReportDataContext from '../../hooks/useReportDataContext';
import type { RootState } from '../../../../state/store';

const ColumnFilterDropdown = ({
  headerName,
  filterKey
}: {
  headerName: any;
  filterKey: string;
}) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const { columnFilters, handleColumnFilterChange, selectedColumnFilters } = useReportDataContext();
  const filter = columnFilters.find((filter) => filter.accessor === filterKey);

  const [selectedValues, setSelectedValues] = useState<{ id: string; name: string }[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    maxHeight?: number;
  }>({ top: 0, left: 0 });
  const dropdownToggleRef = useRef<HTMLElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Currently applied column filters
  const dropdownOptionConfig = useMemo(() => {
    return selectedColumnFilters.find((filter) => filter.key === filterKey) || null;
  }, [selectedColumnFilters, filterKey]);

  // Initialize selectedValues with currently applied filters when dropdown opens
  useEffect(() => {
    if (isOpen && dropdownOptionConfig) {
      setSelectedValues(dropdownOptionConfig.values);
    } else if (!isOpen) {
      setSelectedValues([]);
    }
  }, [isOpen, dropdownOptionConfig]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Filter values based on search term
  const filteredValues =
    filter?.values.filter(
      (value) => value.heading && value.heading.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const handleCheckboxChange = (opt: { value: string; heading: string }, checked: boolean) => {
    if (checked) {
      setSelectedValues((prev) => [...prev, { id: opt.value, name: opt.heading }]);
    } else {
      setSelectedValues((prev) => prev.filter((v) => v.id !== opt.value));
    }
  };

  const handleSubmit = () => {
    handleColumnFilterChange(headerName, filterKey, selectedValues, filter?.url || null);
    setSelectedValues([]);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedValues([]);
    handleColumnFilterChange(headerName, filterKey, [], filter?.url || null);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSearchTerm('');
    setSelectedValues([]);
    setIsOpen(false);
  };

  // Handle dropdown toggle with position calculation
  const handleToggle = (show: boolean) => {
    if (show && dropdownToggleRef.current) {
      const rect = dropdownToggleRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 550; // max height of dropdown (increased for better visibility)

      // Calculate optimal position
      let top = rect.bottom;
      let maxHeight = Math.min(dropdownHeight, spaceBelow - 20); // 20px padding from bottom

      // If not enough space below, try to position above
      if (maxHeight < 350 && spaceAbove > spaceBelow) {
        const availableAbove = Math.min(dropdownHeight, spaceAbove - 20);
        if (availableAbove > maxHeight) {
          top = rect.top - availableAbove;
          maxHeight = availableAbove;
        }
      }

      setDropdownPosition({
        top,
        left: rect.left,
        maxHeight
      });
    }
    setIsOpen(show);
  };

  // Handle position updates on scroll and click outside
  useEffect(() => {
    const updatePosition = () => {
      if (dropdownToggleRef.current && isOpen) {
        const rect = dropdownToggleRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 550;

        let top = rect.bottom;
        let maxHeight = Math.min(dropdownHeight, spaceBelow - 20);

        if (maxHeight < 350 && spaceAbove > spaceBelow) {
          const availableAbove = Math.min(dropdownHeight, spaceAbove - 20);
          if (availableAbove > maxHeight) {
            top = rect.top - availableAbove;
            maxHeight = availableAbove;
          }
        }

        setDropdownPosition({
          top,
          left: rect.left,
          maxHeight
        });
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on toggle or dropdown content
      if (
        (dropdownToggleRef.current && dropdownToggleRef.current.contains(target)) ||
        (dropdownMenuRef.current && dropdownMenuRef.current.contains(target))
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleScroll = () => {
      updatePosition();
    };

    const handleResize = () => {
      updatePosition();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      // Listen for scroll events on all possible containers
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      // Also try to find and listen to ag-Grid specific scroll containers
      const agGridViewports = document.querySelectorAll(
        '.ag-body-viewport, .ag-center-cols-viewport, .ag-body-horizontal-scroll-viewport'
      );
      agGridViewports.forEach((viewport) => {
        viewport.addEventListener('scroll', handleScroll);
      });

      // Update position immediately when dropdown opens
      updatePosition();

      // Cleanup function
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);

        agGridViewports.forEach((viewport) => {
          viewport.removeEventListener('scroll', handleScroll);
        });
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const isChecked = (opt: { value: string; heading: string }) =>
    selectedValues.find((v) => v.id === opt.value) !== undefined;
  const hasActiveFilters = (dropdownOptionConfig?.values.length || 0) > 0;
  const hasChanges = (() => {
    const existingFilter = selectedColumnFilters.find((f) => f.key === filterKey);
    const currentValues = existingFilter ? existingFilter.values : [];

    // Check if lengths differ or any selected value is not in current values
    if (selectedValues.length !== currentValues.length) {
      return true;
    }

    return selectedValues.some(
      (selected) => !currentValues.some((current) => current.id === selected.id)
    );
  })();

  return (
    <StyledDropdownWrapper>
      <div className="column-filter-dropdown">
        <span
          ref={dropdownToggleRef}
          className={`filter-toggle ${hasActiveFilters ? 'has-filters' : ''}`}
          onClick={() => handleToggle(!isOpen)}
        >
          <span className="header-text">{headerName}</span>
          <BsFilterCircle className="filter-icon" />
          {hasActiveFilters && (
            <span className="filter-count">{dropdownOptionConfig?.values.length || 0}</span>
          )}
        </span>

        {isOpen &&
          createPortal(
            <StyledPortalDropdown
              ref={dropdownMenuRef}
              $theme={theme}
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 999,
                minWidth: '320px',
                maxWidth: '400px',
                maxHeight: dropdownPosition.maxHeight ? `${dropdownPosition.maxHeight}px` : '550px',
                padding: 0,
                overflow: 'hidden'
              }}
            >
              <div className="filter-header">
                <h6 className="filter-title">{headerName}</h6>
                {hasActiveFilters && (
                  <Button variant="link" size="sm" onClick={handleClear} className="clear-all-btn">
                    Clear All
                  </Button>
                )}
              </div>

              <div className="search-container">
                <div className="search-input-wrapper">
                  <BsSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Search options..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div
                className="filter-content"
                style={{
                  minHeight: '150px',
                  maxHeight: dropdownPosition.maxHeight
                    ? `${Math.max(dropdownPosition.maxHeight - 180, 150)}px`
                    : '350px'
                }}
              >
                {filteredValues.length ? (
                  <div className="checkbox-container">
                    {filteredValues.map((opt, index) => (
                      <div key={index} className="filter-option">
                        <Form.Check
                          type="checkbox"
                          id={`checkbox-${filterKey}-${index}`}
                          checked={isChecked(opt)}
                          onChange={(e) => handleCheckboxChange(opt, e.target.checked)}
                          className="filter-checkbox"
                        />
                        <label htmlFor={`checkbox-${filterKey}-${index}`} className="filter-label">
                          {opt.heading}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-options">
                    <span>
                      {searchTerm
                        ? `No options found for "${searchTerm}"`
                        : 'No filter options available'}
                    </span>
                  </div>
                )}

                <div className="action-buttons">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCancel}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!hasChanges}
                    className="apply-button"
                  >
                    Apply {selectedValues.length > 0 && `(${selectedValues.length})`}
                  </Button>
                </div>
              </div>
            </StyledPortalDropdown>,
            document.body
          )}
      </div>
    </StyledDropdownWrapper>
  );
};

export default ColumnFilterDropdown;

// Animations
const slideInScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95) translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(13, 138, 106, 0.25);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(13, 138, 106, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(13, 138, 106, 0);
  }
`;

// Styled component for portal dropdown
const StyledPortalDropdown = styled.div<{ $theme: string }>`
  position: fixed;
  z-index: 999;
  min-width: 320px;
  max-width: 400px;
  max-height: 550px;
  padding: 0;
  border: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
  border-radius: 12px;
  background: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};
  box-shadow: 0 4px 15px rgba(0, 0, 0, ${({ $theme }) => ($theme === 'dark' ? '0.3' : '0.1')});
  animation: ${slideInScale} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d' : '#d8edea')};
    border-bottom: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};

    .filter-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: ${({ $theme }) => ($theme === 'dark' ? '#ffffff' : '#374151')};
    }

    .clear-all-btn {
      padding: 4px 8px;
      font-size: 0.8rem;
      text-decoration: none;
      color: #0d8a6c;

      &:hover {
        text-decoration: underline;
        color: #0d8a6c;
      }
    }
  }

  .search-container {
    padding: 16px 20px;
    background: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};
    border-bottom: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};

    .search-input-wrapper {
      position: relative;

      .search-icon {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        font-size: 0.9rem;
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e580' : '#49505780')};
        z-index: 1;
      }

      .search-input {
        width: 100%;
        padding: 8px 12px 8px 35px;
        border: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        border-radius: 6px;
        font-size: 0.9rem;
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
        background: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d' : '#f8f9fa')};
        transition: all 0.2s ease;

        &::placeholder {
          color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e560' : '#49505760')};
        }

        &:focus {
          outline: none;
          border-color: #0d8a6c;
          box-shadow: 0 0 0 3px #0d8a6c20;
        }
      }
    }
  }

  .filter-content {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .checkbox-container {
      flex: 1;
      overflow-y: auto;
      padding: 12px 20px;
      min-height: 120px;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: ${({ $theme }) => ($theme === 'dark' ? '#40404030' : '#dee2e630')};
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        border-radius: 3px;

        &:hover {
          background: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e560' : '#49505760')};
        }
      }

      .filter-option {
        display: flex;
        align-items: center;
        padding: 8px 0;
        transition: all 0.2s ease;
        border-radius: 6px;
        margin: 0 -8px;
        padding-left: 8px;
        padding-right: 8px;

        &:hover {
          background: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d40' : '#f8f9fa40')};
        }

        .filter-checkbox {
          margin: 0;
          margin-right: 12px;

          .form-check-input {
            width: 18px;
            height: 18px;
            border: 2px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
            border-radius: 4px;
            transition: all 0.2s ease;
            background-color: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};

            &:checked {
              background-color: #0d8a6c;
              border-color: #0d8a6c;
            }

            &:focus {
              box-shadow: 0 0 0 3px #0d8a6c20;
            }
          }
        }

        .filter-label {
          flex: 1;
          margin: 0;
          cursor: pointer;
          font-size: 0.9rem;
          color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
          line-height: 1.4;
          user-select: none;

          &:hover {
            color: #0d8a6c;
          }
        }
      }
    }

    .no-options {
      padding: 40px 20px;
      text-align: center;
      color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e580' : '#49505780')};
      font-style: italic;
    }

    .action-buttons {
      display: flex;
      gap: 12px;
      padding: 16px 20px;
      background: ${({ $theme }) => ($theme === 'dark' ? '#1a1a1a' : '#ffffff')};
      border-top: 1px solid ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};

      .cancel-button {
        flex: 1;
        border-color: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
        background-color: ${({ $theme }) => ($theme === 'dark' ? '#2d2d2d' : '#ffffff')};

        &:hover {
          background: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#f8f9fa')};
          color: ${({ $theme }) => ($theme === 'dark' ? '#e5e5e5' : '#495057')};
          border-color: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
        }
      }

      .apply-button {
        flex: 2;
        background: #0d8a6c;
        border-color: #0d8a6c;
        font-weight: 600;
        color: #ffffff;

        &:hover:not(:disabled) {
          background: #0d8a6cdd;
          border-color: #0d8a6cdd;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px #0d8a6c40;
        }

        &:disabled {
          background: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
          border-color: ${({ $theme }) => ($theme === 'dark' ? '#404040' : '#dee2e6')};
          color: ${({ $theme }) => ($theme === 'dark' ? '#999999' : '#6c757d')};
          cursor: not-allowed;
        }
      }
    }
  }
`;

const StyledDropdownWrapper = styled.span`
  .column-filter-dropdown {
    .filter-toggle {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 8px;
      background: ${({ theme }) => theme.dropdown.background_color};
      color: ${({ theme }) => theme.text};
      border: 1px solid ${({ theme }) => theme.border_color};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      user-select: none;
      white-space: nowrap;
      min-width: fit-content;

      &:hover {
        background: ${({ theme }) => theme.button_plain.hover};
        color: ${({ theme }) => theme.button_plain.hover_text};
        border-color: ${({ theme }) => theme.primary_color};
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      &.has-filters {
        background: ${({ theme }) => theme.primary_color}15;
        border-color: ${({ theme }) => theme.primary_color};
        color: ${({ theme }) => theme.primary_color};

        .filter-icon {
          color: ${({ theme }) => theme.primary_color};
          animation: ${pulse} 2s infinite;
        }
      }

      .header-text {
        font-weight: 500;
        font-size: 0.9rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      .filter-icon {
        margin-left: 8px;
        transition: all 0.3s ease;
        font-size: 0.9rem;
      }

      .filter-count {
        position: absolute;
        top: -6px;
        right: -6px;
        background: ${({ theme }) => theme.primary_color};
        color: white;
        border-radius: 50%;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: 600;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
    }

    .filter-dropdown-menu {
      position: fixed;
      z-index: 99999;
      min-width: 320px;
      max-width: 400px;
      max-height: 550px;
      padding: 0;
      border: 1px solid ${({ theme }) => theme.border_color};
      border-radius: 12px;
      background: ${({ theme }) => theme.dropdown.background_color};
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      animation: ${slideInScale} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;

      .filter-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: ${({ theme }) => theme.widget.tableHeader};
        border-bottom: 1px solid ${({ theme }) => theme.border_color};

        .filter-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }

        .clear-all-btn {
          padding: 4px 8px;
          font-size: 0.8rem;
          text-decoration: none;
          color: ${({ theme }) => theme.primary_color};

          &:hover {
            text-decoration: underline;
            color: ${({ theme }) => theme.primary_color};
          }
        }
      }

      .search-container {
        padding: 16px 20px;
        background: ${({ theme }) => theme.widget.background};
        border-bottom: 1px solid ${({ theme }) => theme.border_color};

        .search-input-wrapper {
          position: relative;

          .search-icon {
            position: absolute;
            top: 50%;
            left: 12px;
            transform: translateY(-50%);
            font-size: 0.9rem;
            color: ${({ theme }) => theme.text}80;
            z-index: 1;
          }

          .search-input {
            width: 100%;
            padding: 8px 12px 8px 35px;
            border: 1px solid ${({ theme }) => theme.border_color};
            border-radius: 6px;
            font-size: 0.9rem;
            color: ${({ theme }) => theme.text};
            background: ${({ theme }) => theme.dropdown.background_color};
            transition: all 0.2s ease;

            &::placeholder {
              color: ${({ theme }) => theme.text}60;
            }

            &:focus {
              outline: none;
              border-color: ${({ theme }) => theme.primary_color};
              box-shadow: 0 0 0 3px ${({ theme }) => theme.primary_color}20;
            }
          }
        }
      }

      .filter-content {
        .checkbox-container {
          max-height: 300px;
          overflow-y: auto;
          padding: 12px 20px;

          &::-webkit-scrollbar {
            width: 6px;
          }

          &::-webkit-scrollbar-track {
            background: ${({ theme }) => theme.border_color}30;
            border-radius: 3px;
          }

          &::-webkit-scrollbar-thumb {
            background: ${({ theme }) => theme.border_color};
            border-radius: 3px;

            &:hover {
              background: ${({ theme }) => theme.text}60;
            }
          }

          .filter-option {
            display: flex;
            align-items: center;
            padding: 8px 0;
            transition: all 0.2s ease;
            border-radius: 6px;
            margin: 0 -8px;
            padding-left: 8px;
            padding-right: 8px;

            &:hover {
              background: ${({ theme }) => theme.button_plain.hover}40;
            }

            .filter-checkbox {
              margin: 0;
              margin-right: 12px;

              .form-check-input {
                width: 18px;
                height: 18px;
                border: 2px solid ${({ theme }) => theme.border_color};
                border-radius: 4px;
                transition: all 0.2s ease;

                &:checked {
                  background-color: ${({ theme }) => theme.primary_color};
                  border-color: ${({ theme }) => theme.primary_color};
                }

                &:focus {
                  box-shadow: 0 0 0 3px ${({ theme }) => theme.primary_color}20;
                }
              }
            }

            .filter-label {
              flex: 1;
              margin: 0;
              cursor: pointer;
              font-size: 0.9rem;
              color: ${({ theme }) => theme.text};
              line-height: 1.4;
              user-select: none;

              &:hover {
                color: ${({ theme }) => theme.primary_color};
              }
            }
          }
        }

        .no-options {
          padding: 40px 20px;
          text-align: center;
          color: ${({ theme }) => theme.text}80;
          font-style: italic;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          background: ${({ theme }) => theme.widget.background};
          border-top: 1px solid ${({ theme }) => theme.border_color};

          .cancel-button {
            flex: 1;
            border-color: ${({ theme }) => theme.border_color};
            color: ${({ theme }) => theme.text};

            &:hover {
              background: ${({ theme }) => theme.button_plain.hover};
              color: ${({ theme }) => theme.button_plain.hover_text};
              border-color: ${({ theme }) => theme.border_color};
            }
          }

          .apply-button {
            flex: 2;
            background: ${({ theme }) => theme.primary_color};
            border-color: ${({ theme }) => theme.primary_color};
            font-weight: 600;

            &:hover:not(:disabled) {
              background: ${({ theme }) => theme.primary_color}dd;
              border-color: ${({ theme }) => theme.primary_color}dd;
              transform: translateY(-1px);
              box-shadow: 0 4px 12px ${({ theme }) => theme.primary_color}40;
            }

            &:disabled {
              background: ${({ theme }) => theme.dropdown.disabled.background};
              border-color: ${({ theme }) => theme.dropdown.disabled.background};
              color: ${({ theme }) => theme.dropdown.disabled.color};
              cursor: not-allowed;
            }
          }
        }
      }
    }
  }
`;
